import React, { useCallback, useMemo, useState } from "react";
import { Collapsible } from "../../components";
import { Button } from "../../components/ui";
import { requestTypes } from "../../requests/constants";
import { getFileUrl } from "../../core/config/api";
import {
  formatMoney as formatMoneyShared,
  getBaseCurrency,
} from "../../core/currency";
import { PriceCalculator } from "./PriceCalculator";
import {
  RequestCalculationProvider,
  useRequestCalculationContext,
} from "../../requests/hooks/useRequestCalculation";
import { PxCalculation } from "../../requests/components/molecules/PxCalculation";
import { updateQuotes } from "../../requests/requestsApi";
import { STEPS } from "../constants";
import { SectionTrigger } from "./SectionTrigger";
import { useOrderManagement } from "../useOrderManagement";

import "./px-summary.scss";

// Thin delegate to the shared currency module (no local symbol map) — keeps
// the "null in, null out" guard the lines below rely on.
const formatMoney = (cents, currency) => {
  if (cents == null || cents === "" || isNaN(Number(cents))) return null;
  return formatMoneyShared(cents, {
    currency: currency ?? getBaseCurrency(),
    decimals: 0,
  });
};

function PxSummaryLine({ product, priceCents, priceCurrency, meta }) {
  const thumbId = product?.images?.[0]?.id;
  const brand = product?.brand?.name ?? "—";
  const model = product?.model?.name ?? "";

  return (
    <li className="px-summary-line">
      <div className="px-summary-line__media">
        {thumbId ? (
          <img
            className="px-summary-line__thumb"
            src={getFileUrl(thumbId)}
            alt=""
          />
        ) : (
          <div className="px-summary-line__thumb px-summary-line__thumb--empty" aria-hidden />
        )}
      </div>
      <div className="px-summary-line__body">
        <div className="px-summary-line__title">
          {brand} {model}
        </div>
        {product?.reference_number && (
          <div className="px-summary-line__ref">{product.reference_number}</div>
        )}
        {meta && <div className="px-summary-line__meta">{meta}</div>}
      </div>
      {priceCents != null && (
        <div className="px-summary-line__price">
          {formatMoney(priceCents, priceCurrency)}
        </div>
      )}
    </li>
  );
}

function PxSummarySection({ heading, emptyText, children }) {
  const hasContent = React.Children.count(children) > 0;
  return (
    <section className="px-summary__section">
      <h4 className="px-summary__heading">{heading}</h4>
      {hasContent ? children : emptyText ? (
        <p className="px-summary__empty">{emptyText}</p>
      ) : null}
    </section>
  );
}

function PxSummaryContent({
  client,
  listings,
  sellRequests,
  order,
  request,
  primaryProduct,
  additionalMainRequests,
  additionalMainProducts,
  quotes,
  proceedToPayment,
  onProceed,
}) {
  const {
    results: calculation,
    // Currency the net is denominated in — the banner labels the figure with it
    // rather than assuming the store base.
    dealCurrency,
  } = useRequestCalculationContext();
  // calculateProfit returns [value, errorMessage]; value is {min,max} or 'N/A'.
  const [profitValue, profitErr] = calculation?.profit ?? [];
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [showPaymentsBlockedHint, setShowPaymentsBlockedHint] = useState(false);

  // Mirrors SECTIONS[SECTION_IDS.PAYMENTS].isVisible in
  // requests/v2/sectionConfig.js: Payments stays out of the nav entirely
  // until a listing is linked, either via the quote/request pointer or
  // (once re-quoting has dropped that pointer) via the persisted order's
  // own items. onProceed → navigateTo("payments") silently no-ops when
  // this is false (RequestTemplateV2's stillVisible effect just bounces
  // the selection back) — check it here so we can tell the rep why,
  // instead of Proceed looking like it does nothing.
  const canReachPayments = !!(
    request?.last_quote?.listing_id ||
    request?.linked_listing_id ||
    (order?.items ?? []).some((i) => i?.listing_id)
  );

  const getSellQuote = useCallback(
    (sr) => quotes?.[sr.id] ?? sr.last_quote ?? null,
    [quotes]
  );

  const getSellPrice = useCallback(
    (sellReq) => {
      const quote = sellReq ? getSellQuote(sellReq) : null;
      if (quote?.price != null) {
        // Quote amounts render in the QUOTE's own currency.
        return { cents: quote.price, currency: quote.currency ?? getBaseCurrency() };
      }
      if (sellReq?.price_expectation != null) {
        // Price expectation belongs to the sell REQUEST — its own currency,
        // falling back to the store base (pre-redesign behaviour).
        return {
          cents: sellReq.price_expectation,
          currency: sellReq.currency ?? getBaseCurrency(),
        };
      }
      return null;
    },
    [getSellQuote]
  );

  const mainEntries = useMemo(() => {
    const out = [];
    if (primaryProduct || request) {
      out.push({
        product: primaryProduct ?? request?.product,
        request,
      });
    }
    (additionalMainProducts ?? []).forEach((p, i) => {
      out.push({
        product: p,
        request: additionalMainRequests?.[i] ?? null,
      });
    });
    return out.filter((e) => e?.product);
  }, [primaryProduct, request, additionalMainRequests, additionalMainProducts]);

  const pxProducts = useMemo(
    () => (sellRequests ?? []).map((sr) => sr?.product),
    [sellRequests]
  );

  const hasListings = (listings ?? []).length > 0;

  const mainSummaryEntries = useMemo(() => {
    if (!hasListings) return [];
    const out = mainEntries.map((e, i) => ({
      ...e,
      listing: listings?.[i] ?? null,
    }));
    for (let i = mainEntries.length; i < (listings?.length ?? 0); i++) {
      out.push({
        product: listings[i]?.product ?? null,
        request: null,
        listing: listings[i],
      });
    }
    return out;
  }, [hasListings, mainEntries, listings]);

  const listingSection = hasListings ? (
    <PxSummarySection heading="Listing">
      <ul className="px-summary__lines">
        {mainSummaryEntries.map((e, idx) => {
          const p = e.listing?.product ?? e.product;
          const listing = e.listing;
          return (
            <PxSummaryLine
              key={`px-listing-${idx}-${listing?.id ?? e?.request?.id ?? "x"}`}
              product={p}
              priceCents={listing?.price ?? null}
              // The listing's OWN price currency — not the deal/quote currency
              // (order 87362: £23,500 listing sold on a €26,850 quote). Absent
              // ⇒ store base, which is legacy GBP until multi-currency seeds.
              priceCurrency={listing?.currency ?? getBaseCurrency()}
              meta={listing ? "Listing" : "Order request"}
            />
          );
        })}
      </ul>
    </PxSummarySection>
  ) : (
    <PxSummarySection
      heading="Order Requests"
      emptyText={mainEntries.length === 0 ? "No order products yet." : null}
    >
      {mainEntries.length > 0 && (
        <ul className="px-summary__lines">
          {mainEntries.map(({ product: p, request: r }, idx) => (
            <PxSummaryLine
              key={`main-${idx}-${r?.id ?? "primary"}`}
              product={p}
              priceCents={r?.max_budget ?? null}
              // Budgets render in the REQUEST's currency, falling back to base.
              priceCurrency={r?.currency ?? getBaseCurrency()}
              meta={
                r?.max_budget != null
                  ? "Max budget"
                  : null
              }
            />
          ))}
        </ul>
      )}
    </PxSummarySection>
  );

  const sellSection = (
    <PxSummarySection
      heading="Total Sell"
      emptyText={pxProducts.length === 0 ? "No part-exchange products yet." : null}
    >
      {pxProducts.length > 0 && (
        <ul className="px-summary__lines">
          {pxProducts.map((pxProduct, index) => {
            const sellReq = sellRequests?.[index];
            const price = getSellPrice(sellReq);
            return (
              <PxSummaryLine
                key={`sell-${index}-${pxProduct?.id ?? sellReq?.id ?? "unknown"}`}
                product={pxProduct}
                priceCents={price?.cents ?? null}
                priceCurrency={price?.currency ?? "gbp"}
                meta="Part exchange"
              />
            );
          })}
        </ul>
      )}
    </PxSummarySection>
  );

  return (
    <div className="px-summary request-summary">
      {listingSection}
      {sellSection}

      {/* Live Calculation, as a banner under the deal rather than behind a
          popup. Same component the CHANGE QUOTE modal used, so the two figures
          read identically: "Left to pay <net> by <Client|store|Nobody>" and
          "Profit estimate <min> – <max>". It recomputes from the `quotes` map,
          so picking a quote in any SELECT QUOTE section moves it immediately. */}
      <PxCalculation
        request={request}
        netPay={calculation?.netPay}
        profitValue={profitValue}
        profitErr={profitErr}
        dealCurrency={dealCurrency}
      />

      <div className="px-summary__actions action-buttons">
        {/* CHANGE QUOTE is gone in V2 (onProceed present): each order product
            and each trade-in now has its own SELECT QUOTE section ahead of this
            one, so quoting happens in the flow and PX SUMMARY is purely the
            review. Legacy v1 callers (OrderFlow / InlineOrderFlow, no onProceed)
            keep the popup — it is still their only way to change a quote. */}
        {!onProceed && (
          <Button
            variant="secondary"
            data-btn="px-edit"
            onClick={() => setIsCalculatorOpen(true)}
          >
            Change quote
          </Button>
        )}

        {/*
          In v2 PX SUMMARY is the FINAL review step — the rep reaches it
          AFTER the SELECT QUOTE block, so the order quote exists and the
          net is complete. Proceed advances to PAYMENTS (onProceed is wired
          by RequestSectionView to navigateTo("payments")). We still render
          Proceed unconditionally whenever onProceed is present (v2): even
          if netPay somehow resolves to "N/A" (e.g. a missing quote), the
          rep must never be left with no way forward. Legacy v1 callers
          (no onProceed) fall back to proceedToPayment, which does need a
          resolved netPay — keep the guard only for that path.
        */}
        {(onProceed || calculation?.netPay !== "N/A") && (
          <Button
            variant="primary"
            data-btn="px-confirm"
            onClick={() => {
              if (onProceed && !canReachPayments) {
                setShowPaymentsBlockedHint(true);
                return;
              }
              setShowPaymentsBlockedHint(false);
              onProceed ? onProceed() : proceedToPayment();
            }}
          >
            Proceed
          </Button>
        )}
      </div>

      {showPaymentsBlockedHint && (
        <p className="px-summary__proceed-hint" role="alert">
          Payments needs a listing linked to this deal first — use the{" "}
          <strong>Shopping Cart</strong> step above to connect one, then
          come back and hit Proceed again.
        </p>
      )}

      <PriceCalculator
        {...{
          requestType: requestTypes.PX_CART,
          open: isCalculatorOpen,
          onClose: () => setIsCalculatorOpen(false),
          order,
          product: listings?.[0]?.product,
          request,
          mainEntries: mainEntries.map((e, i) => ({
            ...e,
            listing: listings?.[i] ?? null,
          })),
          pxRequests: sellRequests,
          pxProducts: sellRequests.map(({ product }) => product),
          client,
        }}
      />
    </div>
  );
}

export function PxSummary({
  step,
  lastStep,
  client,
  order,
  request,
  primaryProduct,
  additionalMainRequests,
  additionalMainProducts,
  listings,
  sellRequests,
  quotes,
  setQuotes,
  isPxOrder,
  proceedToPayment,
  onProceed,
  navigateTo,
  confirmRequest,
  mainData,
  requestType,
  journey,
}) {
  const { isInline, setStep } = useOrderManagement();
  const theOrder = useMemo(() => {
    if (order) {
      return order;
    }
    const safeListings = Array.isArray(listings) ? listings : [];
    return {
      total_amount: safeListings[0]?.price,
      items: safeListings.length
        ? safeListings.map((l) => ({ listing: l }))
        : [],
    };
  }, [order, listings]);

  const useRequestContext = useCallback(() => {
    const pxRequestId =
      request?.px_request_id ??
      theOrder?.px_request_id ??
      sellRequests?.[0]?.px_request_id ??
      null;
    return {
      order: theOrder,
      request,
      additionalMainRequests,
      listings,
      pxRequests: sellRequests,
      quotes,
      setQuotes,
      updateQuotes: (newQuotes) => {
        if (pxRequestId) {
          updateQuotes(pxRequestId, newQuotes);
        }
      },
    };
  }, [
    theOrder,
    request,
    additionalMainRequests,
    listings,
    sellRequests,
    quotes,
    setQuotes,
  ]);

  if (lastStep < STEPS.PX_SUMMARY || !isPxOrder) {
    return null;
  }

  return (
    <Collapsible
      trigger={<SectionTrigger icon="pxSummary" label="PX Summary" />}
      open={isInline ? true : step === STEPS.PX_SUMMARY}
      handleTriggerClick={
        isInline
          ? () => setStep(step === STEPS.PX_SUMMARY ? -1 : STEPS.PX_SUMMARY)
          : undefined
      }
    >
      <RequestCalculationProvider
        useRequestContext={useRequestContext}
        isOrderFlow
      >
        <PxSummaryContent
          {...{
            client,
            listings,
            sellRequests,
            order: theOrder,
            request,
            primaryProduct,
            additionalMainRequests,
            additionalMainProducts,
            quotes,
            proceedToPayment,
            onProceed,
          }}
        />
      </RequestCalculationProvider>
    </Collapsible>
  );
}
